import * as Sentry from '@sentry/browser';
import Promise from 'promise-polyfill';
import * as React from 'react';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { HashRouter, Route, Switch, useLocation } from 'react-router-dom';
import { CompatRouter } from "react-router-dom-v5-compat";

import conceptActions from './actions/concepts';
import conceptsFeedbackActions from './actions/conceptsFeedback';
import fillInBlankActions from './actions/fillInBlank';
import lessonActions from './actions/lessons';
import questionActions from './actions/questions';
import titleCardActions from './actions/titleCards.ts';
import Home from './components/home.tsx';
import { quillNormalizer } from '../Shared';
import BackOff from './utils/backOff';
import createStore from './utils/configureStore';

import '../Shared/libs/translations/i18n';

// To add to window
if (!window.Promise) {
  window.Promise = Promise;
}

import { DefaultReactQueryClient, SENTRY_DSN } from '../Shared';

if (window.postbuildEnv.RAILS_ENV === 'production') {
  Sentry.init({ dsn: SENTRY_DSN, environment: 'production' })
} else if (window.postbuildEnv.RAILS_ENV === 'staging') {
  Sentry.init({ dsn: SENTRY_DSN, environment: 'staging' })
}

BackOff();
const store = createStore();
const queryClient = new DefaultReactQueryClient();

// This is pretty hacky.
// Ideally we should really be extracting the both UIDs from
// the Router, but because we populate redux so early in the
// code stack (i.e. right below) we need access to the UIDs
// outside of the Router's scope, so we have this work-around
// rather than shuffling the redux initialization around.
// TODO: At some point we should figure out how to more strictly
// separate the admin and lesson side of the apps so that we
// don't have this unified store definition.
function extractLessonUIDFromLocation() {
  const playRegex = /^#\/play\/(lesson|turk)\/([^\?]+)/;
  const matches = window.location.hash.match(playRegex);
  return (matches) ? matches[2] : null;
}

export const lessonUid = extractLessonUIDFromLocation();

// The GenAI grading tool (/admin/gen-ai) is self-contained and reads none of
// the connect-content store slices, so we skip these fetches on its routes and
// load them lazily the first time any other admin tool is visited.
let connectAdminDataRequested = false;
const requestConnectAdminData = () => {
  if (connectAdminDataRequested) { return; }
  connectAdminDataRequested = true;
  store.dispatch(conceptActions.startListeningToConcepts());
  store.dispatch(conceptsFeedbackActions.loadConceptsFeedback());
  store.dispatch(questionActions.loadQuestions());
  store.dispatch(fillInBlankActions.loadQuestions());
  store.dispatch(lessonActions.startListeningToLessons());
  store.dispatch(titleCardActions.loadTitleCards());
};

const isGenAiPath = (pathname: string) => pathname.startsWith('/admin/gen-ai');
const isPlayPath = (pathname: string) => /^\/play\/(?:lesson|turk)\//.test(pathname);

const ConnectAdminDataLoader = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (isPlayPath(location.pathname) || isGenAiPath(location.pathname)) { return undefined; }

    const timeout = setTimeout(requestConnectAdminData);

    return () => clearTimeout(timeout);
  }, [location.pathname]);

  return null;
};

if (lessonUid) {
  setTimeout(() => {
    store.dispatch(conceptActions.startListeningToConcepts());
    store.dispatch(conceptsFeedbackActions.loadConceptsFeedback());
    store.dispatch(lessonActions.loadLessonWithQuestions(lessonUid));
  });
}

String.prototype.quillNormalize = quillNormalizer;

const route = (
  <CompatRouter>
    <ConnectAdminDataLoader />
    <Switch>
      <Route component={Home} path="/" />
    </Switch>
  </CompatRouter>
);

const App = () => {
  return (
    <QueryClientProvider client={queryClient} contextSharing={true}>
      <Provider store={store}>
        <HashRouter basename="/">{route}</HashRouter>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
